import {
  makeStyles,
  TabList,
  type SelectTabData,
} from '@fluentui/react-components';
import { useEffect, useRef, type ReactNode } from 'react';

const useStyles = makeStyles({
  scroller: {
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
      height: '0px',
    },
  },
  list: {
    width: 'max-content',
  },
});

const ScrollableTabList = ({
  selectedValue,
  onTabSelect,
  children,
}: {
  selectedValue: string;
  onTabSelect: (value: string) => void;
  children: ReactNode;
}) => {
  const styles = useStyles();
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth || event.deltaY === 0) return;
      el.scrollLeft += event.deltaY;
      event.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    const selected = el?.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]'
    );
    if (!el || !selected) return;
    const tabBox = selected.getBoundingClientRect();
    const box = el.getBoundingClientRect();
    if (tabBox.left < box.left) el.scrollLeft -= box.left - tabBox.left;
    else if (tabBox.right > box.right)
      el.scrollLeft += tabBox.right - box.right;
  }, [selectedValue]);

  const selectTab = (_event: unknown, data: SelectTabData) => {
    if (typeof data.value === 'string') onTabSelect(data.value);
  };

  return (
    <div className={styles.scroller} ref={scrollerRef}>
      <TabList
        className={styles.list}
        selectedValue={selectedValue}
        onTabSelect={selectTab}
      >
        {children}
      </TabList>
    </div>
  );
};

export default ScrollableTabList;
